import { IWindow } from '../../../../services/analytics/adapters/mparticle'
import { Utils } from '../../../../utils/common'
import { ErrorFormatter } from './formatters/error'

declare let window: IWindow

const ERR_MSG = 'Empty object is not acceptable'

export class PageEvents {

    /**
     * Custom event types
     * https://docs.mparticle.com/developers/sdk/web/event-tracking/#custom-event-type
     */

    public static mpEventType = {
        Location: 2,
        Navigation: 1,
        Other: 8,
        Search: 3,
        Social: 7,
        Transaction: 4,
        UserContent: 5,
        UserPreference: 6,
    }

    /**
     * Custome event names
     */

    public static mpEventName = {
        AccountConfirmed: 'Create Account/Email Confirmed',
        AccountModal: 'Create Account Modal',
        Button: 'Button Click',
        ConsentBanner: 'GDPR Consent Banner View',
        CreditCardInfo: 'View Enter Credit Card Information Module',
        Default: 'Default Event',
        Error: 'Error Message Displayed',
        ExternalCampaign: 'External Campaign',
        InternalCampaign: 'Internal Campaign',
        InternalPromotionClick: 'Internal Promotion Click',
        NavigationClick: 'Navigation Click',
        NewReg: 'New Registration',
        PercentPageview: 'Percentage of Page Viewed',
        PhotoGallery: 'Photo Gallery',
        RegThankScreen: 'View Registration Thank You Screen',
        ScreenView: 'Screen View',
        Search: 'Search',
        Signin: 'Sign In',
        SigninRegModule: 'View Sign In/Registration Module',
        Signout: 'Sign Out',
        SocialShare: 'Share Article',
        SubscribeNewsletter: 'Newsletter Subscribe',
        SubscriptionButton: 'Subscription Button Click',
        VerificationScreen: 'View Verification Request Screen',
        ViafouraComment: 'Viafoura - User Comment',
        ViafouraDislike: 'Viafoura - User Dislike',
        ViafouraLike: 'Viafoura - User Like',
        ViafouraUserUpdate: 'Viafoura - User Updated',
        VideoCenter: 'Video Center',
        VideoEnded: 'Video Ended',
        VideoFullScreen: 'Video Full Screen Activated',
        VideoPaused: 'Video Paused',
        VideoPlayerError: 'Video Player Error',
        VideoStarted: 'Video Clicked/Started',
        ViewArticle: 'View Article',
        ViewCategory: 'View Category',
        WidgetClicked: 'Widget Click',
        WidgetInView: 'Widget in View',
        WidgetVideoLoad: 'Widget Video Load - Story'
    }

    public errorFormatter = new ErrorFormatter()

    /**
     * Implement mParticle's logPageView - https://docs.mparticle.com/developers/sdk/web/screen-tracking/
     * @param: string, object, object
     * @returns: boolean
     */

    public logPageView(EventType: string, properties: object, customFlag?: object): boolean {
        try {
            if (Utils.isEmptyObject(properties)) {
                this.logCustomError(ERR_MSG, 'logPageView')
                return false
            }
            const args = [EventType, properties, customFlag]
            window.mParticle.ready(function() {
                window.mParticle.logPageView.apply(this, args)
            })
            return true
        } catch (e) {
            return false
        }
    }

    /**
     * Implement mParticle's logEvent - https://docs.mparticle.com/developers/sdk/web/event-tracking/
     * @param: string, number, object, object
     * @returns: boolean
     */

    public logEvent(EventName: string, EventType: number, properties: object, customFlag?: object): boolean {
        try {
            if (Utils.isEmptyObject(properties)) {
                this.logCustomError(ERR_MSG, 'logEvent')
                return false
            }
            const args = [EventName, EventType, properties, customFlag]
            window.mParticle.ready(function() {
                window.mParticle.logEvent.apply(this, args)
            })
            return true
        } catch (e) {
            return false
        }
    }

    /**
     * Custom log event to log errors into mParticle
     * https://docs.mparticle.com/developers/sdk/web/event-tracking/
     */

    public logCustomError(error: string, eventName: string): void {
        const errorMessage = this.errorFormatter.error(error, eventName)
        this.logEvent('Log error', PageEvents.mpEventType.Other, errorMessage)
    }

    /**
     * Implement mParticle's logEvent - https://docs.mparticle.com/developers/sdk/web/event-tracking/
     * as default event. If an event match is not found, this event will be fired
     * @param: string, number, object, object
     * @returns: boolean
     */

    public logDefaultEvent(properties: object): boolean {
        return this.logEvent(PageEvents.mpEventName.Default, PageEvents.mpEventType.Other, properties)
    }
}
